import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import size from 'lodash/size';
import { ArrowDownSecond, ArrowRight, ArrowUp, DropDownArrow } from '@yme/assets/icons';
import { LabelStyled } from '@yme/atoms/input';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { FilterType } from '@yme/common/constants';
import { useOutsideClick, useWindowSize } from '@yme/common/hooks';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreWhite1000 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel3 } from '@yme/tokens/typography';
import { EVENT_NAME_MAP } from '@yme/utils/constants';
import { FilterDropdownContext } from './filterDropdownContext';
import { ButtonFilterStyled, CloseIconStyled, DropDownFilterStyled, FilterButtonStyled, FilterButtonWrapperStyled, FilterChildWrapper, FilterDropdownWrapperStyled, FilterLabelStyled, FilterMenuItemStyled, FlexWrapperStyled, ListStyled, } from './styled';
export const FilterDropdown = memo(({ titleLabel, emptyPlaceholderLabel, selectedPlaceholderLabel, totalCountLabels, dataTestId = '', options: incomingOptions, children, isHorizontal = false, isDisabled = false, isRightToLeft, type = FilterType.CHECKLIST, placeHolderLabel = '', isListItemView = false, isCustomVerticalView = false, countAppliedValues, isBtnBorderTopNeeded = true, withTotalCountPlaceholder = false, isTitleFormLabel = false, isTitleLabelRequired = false, setTouched, }) => {
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [options, setOptions] = useState(incomingOptions);
    const contextValue = useMemo(() => ({
        options,
        isExpanded,
        titleLabel,
        setOptions,
        setIsExpanded,
    }), [isExpanded, options, titleLabel]);
    const onToggleDropdown = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsExpanded(!isExpanded);
        setTouched?.();
    }, [isExpanded, setTouched]);
    const closeDropdown = useCallback(() => {
        if (isDesktop) {
            setIsExpanded(false);
        }
    }, [isDesktop]);
    const { elementRef: containerRef } = useOutsideClick({
        eventName: EVENT_NAME_MAP.click,
        callback: closeDropdown,
    });
    useEffect(() => {
        setOptions(incomingOptions);
    }, [incomingOptions]);
    const counter = size(filter(options, ({ checked }) => checked));
    // todo: md: Try to avoid separating logic based on filter type,
    // FilterDropdown shouldn't know about types of children.
    // It's better to create another label prop and generate it outside.
    const getPlaceHolder = () => {
        if (placeHolderLabel) {
            return placeHolderLabel;
        }
        if (counter === 0) {
            return emptyPlaceholderLabel;
        }
        const allSelected = counter === options.length;
        const countTextWithTotal = allSelected
            ? totalCountLabels?.all
            : `(${counter}) ${totalCountLabels?.of} (${options.length}) ${selectedPlaceholderLabel}`;
        const selectionCountPlaceholder = withTotalCountPlaceholder
            ? countTextWithTotal
            : `(${counter}) ${selectedPlaceholderLabel}`;
        return type === FilterType.CHECKLIST
            ? selectionCountPlaceholder
            : find(options, ({ checked }) => checked)?.label;
    };
    // Custom Vertical View (use on PLP filters)
    if (isCustomVerticalView) {
        const currentLabel = countAppliedValues
            ? `${titleLabel} (${countAppliedValues})`
            : titleLabel;
        return (React.createElement(FilterDropdownContext.Provider, { value: contextValue },
            React.createElement(DropDownFilterStyled, { flexDirection: "column", bg: colorCoreWhite1000, isBorderTopNeeded: isBtnBorderTopNeeded },
                React.createElement(ButtonFilterStyled, { padding: "16px", justifyContent: "space-between", as: "button", type: "button", tabIndex: 0, onClick: onToggleDropdown, "aria-label": t('common_buttonForExpandingBlock_accessibility') },
                    React.createElement(RegularTextLevel3, { isUserSelectDisabled: true }, currentLabel),
                    React.createElement(SVGIcon, { type: isExpanded ? ArrowUp : ArrowDownSecond, size: ICON_SIZE.s })),
                React.createElement(FlexWrapperStyled, { isExpanded: isExpanded },
                    React.createElement(ListStyled, { as: "ul", flexDirection: "column", isFullWidth: true, isRightToLeft: isRightToLeft }, children)))));
    }
    if (isListItemView) {
        const currentLabel = countAppliedValues
            ? `${titleLabel} (${countAppliedValues})`
            : titleLabel;
        return (React.createElement(FilterDropdownContext.Provider, { value: contextValue },
            React.createElement(FilterMenuItemStyled, { onClick: onToggleDropdown },
                React.createElement(FlexWrapper, { justifyContent: "space-between" },
                    React.createElement(RegularTextLevel3, { as: "span" }, currentLabel),
                    React.createElement(SVGIcon, { type: ArrowRight }))),
            children));
    }
    return (React.createElement(FilterDropdownWrapperStyled, { "data-testid": dataTestId, gap: isHorizontal ? '16px' : '', flexDirection: isHorizontal ? 'row' : 'column' },
        React.createElement(FilterLabelStyled, { isHorizontal: isHorizontal, isTitleFormLabel: isTitleFormLabel }, isTitleFormLabel ? (React.createElement(LabelStyled, { isRequired: isTitleLabelRequired }, titleLabel)) : (React.createElement("span", null, titleLabel))),
        React.createElement(FilterDropdownContext.Provider, { value: contextValue },
            React.createElement(FilterButtonWrapperStyled, { isDisabled: isDisabled, ref: containerRef },
                React.createElement(FilterButtonStyled, { isRightToLeft: isRightToLeft, onClick: onToggleDropdown }, getPlaceHolder()),
                React.createElement(CloseIconStyled, { isDisabled: isDisabled, iconRotate: isExpanded ? 0 : 180, type: DropDownArrow, isRightToLeft: isRightToLeft }),
                React.createElement(FilterChildWrapper, null, children)))));
});
FilterDropdown.displayName = 'FilterDropdown';
